import { ChronoUnit, DayOfWeek, Instant, ZonedDateTime, ZoneId } from "@js-joda/core";
import { TemplateLoadingType } from "utils/enums";
import { addToDate, DweenTimeUnitEnum, PossibleDateTypes, toInstant, toInstantOrNow, truncateDate } from "utils/time-utils";
import { LoadingConfig } from "./interfaces";

const getTemplateLoadingRange = (config: LoadingConfig, referenceTime: Instant | undefined, zoneId: ZoneId) => {
  if (config.loadingType === TemplateLoadingType.EVERYTHING) {
    return {
      start: new Date(),
      end: new Date(),
    };
  }

  const nowTime = toInstantOrNow(referenceTime).toDate();

  const truncateStartUnit = config.loadingConfig?.truncateStartUnit || DweenTimeUnitEnum.DAYS; // or use periodUnit?? // TODO handle possible error
  const periodUnit = config.loadingConfig?.periodUnit ?? DweenTimeUnitEnum.DAYS;
  const offsetUnit = config.loadingConfig?.offsetUnit ?? periodUnit;

  const period = config.loadingConfig?.period ?? 1; // truncate period (1,2,7,etc...) // TODO handle possible error
  const offset = config.loadingConfig?.offset ?? 0;

  const nowTruncated = truncateDate(nowTime, truncateStartUnit, zoneId);

  const from = addToDate(nowTruncated, offset, offsetUnit);
  const to = addToDate(from, period, periodUnit);

  // Ensure that range sequence is logical
  const startRange = from > to ? to : from;
  const endRange = from > to ? from : to;

  return {
    start: startRange,
    end: endRange,
  };
};

const swapDates = <T extends PossibleDateTypes>(first: T, second: T) => {
  const firstMillis = toInstant(first).toEpochMilli();
  const secondMillis = toInstant(second).toEpochMilli();

  if (firstMillis > secondMillis) {
    return { from: second, to: first };
  }
  return { from: first, to: second };
};

const alignTo = (date: ZonedDateTime, unit: ChronoUnit): ZonedDateTime => {
  if (unit.compareTo(ChronoUnit.DAYS) <= 0) {
    return date.truncatedTo(unit) ;
  }

  if (unit === ChronoUnit.WEEKS) {
    return date.with(DayOfWeek.MONDAY).truncatedTo(ChronoUnit.DAYS) ;
  }

  if (unit === ChronoUnit.MONTHS) {
    return date.withDayOfMonth(1).truncatedTo(ChronoUnit.DAYS);
  }

  if (unit === ChronoUnit.YEARS) {
    return date.withDayOfYear(1).truncatedTo(ChronoUnit.DAYS);
  }

  return date;
};

export { getTemplateLoadingRange, swapDates, alignTo };
